import { baseApi, Http, rtkq, RtkqRequest } from '@app/base';
import { GetAppointmentTypeTemplateDocument, GetAppointmentTypeTemplatesDocument } from './graphql';
import {
  AppointmentTypeTemplate,
  CreateAppointmentTypeTemplateRequest,
  UpdateAppointmentTypeTemplateRequest,
} from './appointmentTypeTemplatesTypes';
import { FilterRule } from '@webinex/asky';

export const appointmentTypeTemplatesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAllAppointmentTypeTemplates: build.query<
      AppointmentTypeTemplate[],
      RtkqRequest<{ filterRule?: FilterRule | null }>
    >({
      queryFn: rtkq.graphql(GetAppointmentTypeTemplatesDocument, {
        select: (x) => x.appointmentTypeTemplates as AppointmentTypeTemplate[],
        vars: ({ filterRule }) => ({
          filter: filterRule ? JSON.stringify(filterRule) : null,
        }),
      }),
      providesTags: (result = []) => [
        { type: 'medical-record:appointment-type-template-collection', id: 'list' },
        ...result.flatMap(({ id }) => ({
          type: 'medical-record:appointment-type-template-collection' as const,
          id: id,
        })),
      ],
    }),

    getAppointmentTypeTemplate: build.query<AppointmentTypeTemplate, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetAppointmentTypeTemplateDocument, {
        select: (x) => x.appointmentTypeTemplate as AppointmentTypeTemplate,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [
        {
          type: 'medical-record:appointment-type-template' as const,
          id: id,
        },
      ],
    }),

    createAppointmentTypeTemplate: build.mutation<string, RtkqRequest<CreateAppointmentTypeTemplateRequest>>({
      queryFn: rtkq.axios((args) => Http.post('/api/appointment-type-template', args)),
      invalidatesTags: () => [
        { type: 'medical-record:appointment-type-template', id: 'list' },
        { type: 'medical-record:appointment-type-template-collection', id: 'list' },
      ],
    }),

    updateAppointmentTypeTemplate: build.mutation<string, RtkqRequest<UpdateAppointmentTypeTemplateRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/appointment-type-template/${args.id}`, args)),
      invalidatesTags: (_, __, { id }) => [
        { type: 'medical-record:appointment-type-template-collection', id: 'list' },
        { type: 'medical-record:appointment-type-template', id: id },
      ],
    }),

    deleteAppointmentTypeTemplate: build.mutation<string, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.axios((args) => Http.delete(`/api/appointment-type-template/${args.id}`)),
      invalidatesTags: (_, __, { id }) => [
        { type: 'medical-record:appointment-type-template-collection', id: 'list' },
        { type: 'medical-record:appointment-type-template', id: id },
      ],
    }),
  }),
});

export const {
  useGetAllAppointmentTypeTemplatesQuery,
  useLazyGetAllAppointmentTypeTemplatesQuery,
  useGetAppointmentTypeTemplateQuery,
  useCreateAppointmentTypeTemplateMutation,
  useUpdateAppointmentTypeTemplateMutation,
  useDeleteAppointmentTypeTemplateMutation,
} = appointmentTypeTemplatesApi;
